import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"
import type { CogoLayout } from "../core/types"
import { resolveInteractionClasses } from "../runtime/interaction-resolver"
import { spacingToken } from "../lib/design-tokens"

export function ContainerRenderer({ element, children: propChildren }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()

  // Merge element-level responsive into layout for resolveLayoutClasses
  const effectiveLayout = (() => {
    const resp = element.responsive as Record<string, Record<string, unknown>> | undefined
    if (!resp || !element.layout) return element.layout
    // element.responsive: { "sm": { "layout": { "columns": 1 } } }
    // → merge into layout.responsive: { "sm": { "columns": 1 } }
    const layoutResp: Record<string, Record<string, unknown>> = {}
    for (const [bp, overrides] of Object.entries(resp)) {
      const layoutOverrides = (overrides as Record<string, unknown>).layout as Record<string, unknown> | undefined
      if (layoutOverrides) {
        layoutResp[bp] = layoutOverrides
      }
    }
    if (Object.keys(layoutResp).length === 0) return element.layout
    return { ...element.layout, responsive: { ...(element.layout as unknown as Record<string, unknown>).responsive as object, ...layoutResp } } as CogoLayout
  })()

  const layoutClasses = resolveLayoutClasses(effectiveLayout)
  const spacingClasses = element.padding ? resolvePadding(element.padding) : ""
  const gapClasses = element.gap ? `gap-${spacingToken(element.gap)}` : ""
  // Build inline styles from styling properties
  // Detect when className already handles specific CSS properties via Tailwind utilities.
  // Bridge (rehype) pipeline generates BOTH className AND structured options for the same properties.
  // Inline styles always beat Tailwind classes (highest CSS specificity), which breaks responsive
  // overrides like lg:max-w-none being overridden by inline max-width: 672px.
  const cls = element.className ?? ""
  const clsHandlesMaxWidth = /\bmax-w-/.test(cls)
  const clsHandlesBg = /\bbg-(?!transparent\b|inherit\b|current\b)/.test(cls)

  const inlineStyle: React.CSSProperties = {}
  if (element.maxWidth && !clsHandlesMaxWidth) inlineStyle.maxWidth = element.maxWidth
  const styling = element.styling as Record<string, unknown> | undefined
  if (styling?.minHeight) inlineStyle.minHeight = `${styling.minHeight}px`
  if (styling?.width) inlineStyle.width = typeof styling.width === "number" ? `${styling.width}px` : String(styling.width)
  if (styling?.height) inlineStyle.height = typeof styling.height === "number" ? `${styling.height}px` : String(styling.height)
  if (styling?.opacity != null) inlineStyle.opacity = Number(styling.opacity)
  // Resolve background from styling.background (color or gradient)
  if (styling?.background && !clsHandlesBg) {
    const bg = styling.background as Record<string, unknown>
    if (bg.color) {
      const resolvedBg = String(resolveSymbol(String(bg.color)) ?? "")
      if (resolvedBg.startsWith("#") && resolvedBg.length >= 7) {
        if (bg.opacity != null && Number(bg.opacity) < 1) {
          // Convert hex + opacity → rgba
          const hex = resolvedBg.replace("#", "")
          const r = parseInt(hex.substring(0, 2), 16)
          const g = parseInt(hex.substring(2, 4), 16)
          const b = parseInt(hex.substring(4, 6), 16)
          inlineStyle.backgroundColor = `rgba(${r},${g},${b},${Number(bg.opacity)})`
        } else {
          inlineStyle.backgroundColor = resolvedBg
        }
      } else if (resolvedBg === "transparent") {
        inlineStyle.backgroundColor = "transparent"
      } else if (resolvedBg) {
        // Accept CSS variable references (hsl(var(--card))), rgb(), named colors, etc.
        inlineStyle.backgroundColor = resolvedBg
      }
    }
    if (bg.gradient) {
      const grad = bg.gradient as Record<string, unknown>
      const stops = grad.stops as Array<Record<string, unknown>> | undefined
      if (stops && stops.length >= 2) {
        const colorStops = stops.map(s => {
          const rawColor = String(s.color ?? "")
          const resolved = rawColor.includes("#{") ? String(resolveSymbol(rawColor) ?? "") : rawColor
          const opacity = s.opacity != null ? Number(s.opacity) : 1
          const pos = s.position != null ? `${s.position}%` : ""
          if (opacity < 1 && resolved.startsWith("#")) {
            // Convert hex + opacity → rgba
            const hex = resolved.replace("#", "")
            const r = parseInt(hex.substring(0, 2), 16)
            const g = parseInt(hex.substring(2, 4), 16)
            const b = parseInt(hex.substring(4, 6), 16)
            return `rgba(${r},${g},${b},${opacity}) ${pos}`.trim()
          }
          return `${resolved} ${pos}`.trim()
        })
        if (grad.type === "radial") {
          inlineStyle.background = `radial-gradient(circle, ${colorStops.join(", ")})`
        } else {
          const dirMap: Record<string, string> = {
            "to-bottom": "to bottom", "to-right": "to right",
            "to-bottom-right": "to bottom right", "to-top": "to top",
          }
          const dir = dirMap[String(grad.direction ?? "to-bottom")] ?? "to bottom"
          inlineStyle.background = `linear-gradient(${dir}, ${colorStops.join(", ")})`
        }
      }
    }
  }
  // Resolve border color from styling.border.color (theme tokens)
  if (styling?.border) {
    const border = styling.border as Record<string, unknown>
    if (border.color) {
      const resolvedBorderColor = String(resolveSymbol(String(border.color)) ?? "")
      if (resolvedBorderColor) {
        inlineStyle.borderColor = resolvedBorderColor
      }
    }
    if (border.width && Number(border.width) >= 1) {
      inlineStyle.borderWidth = `${border.width}px`
      inlineStyle.borderStyle = "solid"
    }
  }
  // maxWidth from styling — skip when className already has max-w-* (avoids blocking responsive overrides)
  if (styling?.maxWidth && !clsHandlesMaxWidth) {
    inlineStyle.maxWidth = typeof styling.maxWidth === "number" ? `${styling.maxWidth}px` : String(styling.maxWidth)
  }
  // aspectRatio from styling
  if (styling?.aspectRatio) {
    const ar = String(styling.aspectRatio)
    inlineStyle.aspectRatio = ar.replace(":", "/")
  }
  // overflow hidden
  if (styling?.overflow === "hidden") {
    inlineStyle.overflow = "hidden"
  }
  // flex grow from styling
  if (styling?.flex != null) {
    inlineStyle.flex = Number(styling.flex)
  }
  // Resolve element.transform → inline CSS transform
  if (element.transform) {
    const t = element.transform
    const parts: string[] = []
    if (t.scale != null) parts.push(`scale(${t.scale})`)
    else if (t.scaleX != null || t.scaleY != null) parts.push(`scale(${t.scaleX ?? 1}, ${t.scaleY ?? 1})`)
    if (t.rotate != null) parts.push(`rotate(${t.rotate}deg)`)
    if (t.translateX != null) parts.push(`translateX(${t.translateX}px)`)
    if (t.translateY != null) parts.push(`translateY(${t.translateY}px)`)
    if (t.skewX != null) parts.push(`skewX(${t.skewX}deg)`)
    if (t.skewY != null) parts.push(`skewY(${t.skewY}deg)`)
    if (parts.length) inlineStyle.transform = parts.join(" ")
    if (t.origin) inlineStyle.transformOrigin = t.origin
  }

  // Resolve element.transition → inline CSS transition
  if (element.transition) {
    const tr = element.transition
    const props = tr.properties?.join(", ") ?? "all"
    const dur = `${tr.duration ?? 150}ms`
    const ease = tr.curve ?? "ease"
    inlineStyle.transition = `${props} ${dur} ${ease}${tr.delay ? ` ${tr.delay}ms` : ""}`
  }

  const hasInlineStyle = Object.keys(inlineStyle).length > 0

  // Resolve variant
  const variantMap: Record<string, string> = {
    card: "rounded-xl border bg-card text-card-foreground shadow",
    header: "sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
    bordered: "rounded-md border",
  }
  const variantClasses = element.variant ? variantMap[element.variant] ?? "" : ""

  // Resolve position — support all CSS position types + auto-relative if any child is absolute
  const hasAbsoluteChild = element.children?.some((c) => c.position?.type === "absolute") ?? false
  const positionClasses = (() => {
    const posType = element.position?.type
    const classes: string[] = []

    if (posType === "sticky") classes.push("sticky")
    else if (posType === "fixed") classes.push("fixed")
    else if (posType === "absolute") classes.push("absolute")
    else if (posType === "relative" || hasAbsoluteChild) classes.push("relative")

    // Apply top/right/bottom/left offset values from position
    if (posType && posType !== "relative") {
      const pos = element.position!
      if (pos.top != null) classes.push(`top-${pos.top}`)
      if (pos.right != null) classes.push(`right-${pos.right}`)
      if (pos.bottom != null) classes.push(`bottom-${pos.bottom}`)
      if (pos.left != null) classes.push(`left-${pos.left}`)
    }

    return classes.join(" ")
  })()

  // Resolve interaction classes via typed InteractionConfig resolver
  const interactionClasses = resolveInteractionClasses(element.interaction)

  // Resolve tap event → onClick
  const tapHandler = element.events?.tap
  const isNavigateAction = typeof tapHandler === "object" && tapHandler.action === "navigate" && tapHandler.params
  const navigateRoute = isNavigateAction
    ? String(resolveSymbol(tapHandler.params!.route as string) ?? "")
    : undefined

  const handleClick = tapHandler
    ? (e: React.MouseEvent) => {
        if (isNavigateAction && navigateRoute) {
          e.preventDefault()
          // Dispatch navigate event with resolved route and item context
          dispatchEvent(element.id, "tap", { __resolvedRoute: navigateRoute, ...itemContext })
        } else if (typeof tapHandler === "string") {
          dispatchEvent(element.id, "tap", itemContext)
        }
      }
    : undefined

  // Fill property for buttons — flex-1
  const fillClasses = element.fill ? "flex-1" : ""

  // Margin passthrough (Tailwind margin classes like mb-2, mt-1)
  // Guard: if margin is still an object (missed by normalizeElement), skip it to prevent clsx bugs
  const marginClasses = (typeof element.margin === "string") ? element.margin : ""

  // Grid col-span (for grid children)
  // Note: gridSpan { column, row } is now handled in normalizeElement → className
  const colSpanClasses = (() => {
    if (!element.colSpan) return ""
    if (typeof element.colSpan === "number") return `col-span-${element.colSpan}`
    return Object.entries(element.colSpan)
      .map(([bp, span]) => bp === "default" ? `col-span-${span}` : `${bp}:col-span-${span}`)
      .join(" ")
  })()

  const className = cn(layoutClasses, spacingClasses, gapClasses, variantClasses, positionClasses, interactionClasses, fillClasses, marginClasses, colSpanClasses, element.className)

  // Use prop children (from IteratedContainer) or element.children
  const children = propChildren ?? element.children?.map((child, idx) => (
    <ElementRenderer key={child.id ?? `child-${idx}`} element={child} />
  ))

  // Header variant: wrap children in a max-w container
  // Preserve element.className on the outer <header> so classes like `group` are
  // emitted on the DOM (required for group-hover: utilities on child elements).
  if (element.variant === "header") {
    return (
      <header className={cn(variantClasses, interactionClasses, element.className)}>
        <div className={cn("mx-auto flex h-14 max-w-7xl items-center px-4 sm:px-6 lg:px-8", layoutClasses, spacingClasses, gapClasses)}>
          {children}
        </div>
      </header>
    )
  }

  // Render as <a> for navigate actions (better semantics + accessibility)
  if (isNavigateAction && navigateRoute) {
    return (
      <a
        href={navigateRoute}
        className={className}
        style={hasInlineStyle ? inlineStyle : undefined}
        onClick={handleClick}
      >
        {children}
      </a>
    )
  }

  const semanticTags = new Set(["section", "header", "footer", "nav", "main", "aside", "article"])
  const Tag = (element.htmlTag ?? element.tag ?? (semanticTags.has(element.type) ? element.type : "div")) as keyof JSX.IntrinsicElements

  return (
    <Tag
      className={className}
      style={hasInlineStyle ? inlineStyle : undefined}
      onClick={handleClick}
    >
      {children}
    </Tag>
  )
}

function resolveLayoutClasses(layout?: CogoLayout): string {
  if (!layout) return ""
  const raw = layout as unknown as Record<string, unknown>
  const classes: string[] = []

  // Support both property name formats:
  //   layout.align / layout.justify (CogoLayout standard)
  //   layout.alignItems / layout.justifyContent (CSS/JSON factory format)
  const align = layout.align ?? (raw.alignItems as string | undefined)
  const justify = layout.justify ?? (raw.justifyContent as string | undefined)
  const gap = layout.gap ?? (raw.gap as string | number | undefined)

  if (layout.type === "flex") {
    classes.push("flex")
    if (layout.direction === "column") classes.push("flex-col")
    if (align) classes.push(`items-${align}`)
    if (justify) classes.push(`justify-${justify}`)
    if (gap != null) classes.push(`gap-${spacingToken(gap)}`)
    if (layout.wrap) classes.push("flex-wrap")
  } else if (layout.type === "grid") {
    classes.push("grid")
    if (gap != null) classes.push(`gap-${spacingToken(gap)}`)
    if (align) classes.push(`items-${align}`)
    // Grid rows
    const rows = (raw as Record<string, unknown>).rows as number | undefined
    if (rows) classes.push(`grid-rows-${rows}`)
    // Grid columns: pass through as mobile-first Tailwind classes
    // JSON: { columns: N, responsive: { sm: { columns: M }, lg: { columns: P } } }
    // → grid-cols-N sm:grid-cols-M lg:grid-cols-P
    if (layout.columns) {
      classes.push(`grid-cols-${layout.columns}`)
      if (layout.responsive) {
        for (const [bp, ov] of Object.entries(layout.responsive)) {
          if (ov.columns != null) {
            classes.push(`${bp}:grid-cols-${ov.columns}`)
          }
        }
      }
    }
  }

  // Responsive breakpoint overrides (non-column properties)
  if (layout.responsive) {
    for (const [breakpoint, overrides] of Object.entries(layout.responsive)) {
      // columns handled above in mobile-first conversion
      if (overrides.direction === "row") classes.push(`${breakpoint}:flex-row`)
      if (overrides.direction === "column") classes.push(`${breakpoint}:flex-col`)
      if (overrides.align) classes.push(`${breakpoint}:items-${overrides.align}`)
      if (overrides.justify) classes.push(`${breakpoint}:justify-${overrides.justify}`)
      if (overrides.gap) classes.push(`${breakpoint}:gap-${spacingToken(overrides.gap)}`)
    }
  }

  return classes.join(" ")
}

/** Parse compound padding: "24 16" → "py-24 px-16", "4" → "p-4", {top,bottom,left,right} → directional */
function resolvePadding(padding: string | number | Record<string, unknown>): string {
  if (typeof padding === "number") return `p-${spacingToken(padding)}`
  // Handle object padding: { top, bottom, left, right } from rehype pipeline
  if (typeof padding === "object" && padding !== null) {
    const p = padding as Record<string, number | undefined>
    const parts: string[] = []
    if (p.top != null) parts.push(`pt-${spacingToken(p.top)}`)
    if (p.right != null) parts.push(`pr-${spacingToken(p.right)}`)
    if (p.bottom != null) parts.push(`pb-${spacingToken(p.bottom)}`)
    if (p.left != null) parts.push(`pl-${spacingToken(p.left)}`)
    return parts.join(" ")
  }
  const str = String(padding).trim()
  const splitParts = str.split(/\s+/)
  if (splitParts.length === 1) return `p-${spacingToken(splitParts[0])}`
  if (splitParts.length === 2) {
    return `py-${spacingToken(splitParts[0])} px-${spacingToken(splitParts[1])}`
  }
  if (splitParts.length === 4) {
    return `pt-${spacingToken(splitParts[0])} pr-${spacingToken(splitParts[1])} pb-${spacingToken(splitParts[2])} pl-${spacingToken(splitParts[3])}`
  }
  return `p-${spacingToken(splitParts[0])}`
}

